/**
 * Agent-handoff marker parsing.
 *
 * A handoff writes two marker messages — an OUTBOUND card in the origin thread
 * and an INBOUND card in the new one — both stamped with
 * `message_metadata.kind === 'agent_branch'`. This module is the single place
 * that turns that raw metadata into the typed `AgentHandoff` the UI renders,
 * so every mapping site in conversationService agrees on the shape.
 */

import type { AgentHandoff, AgentHandoffParty } from '@/types/models';

/** Must match HANDOFF_MARKER_KIND in backend conversation_service.py. */
export const HANDOFF_MARKER_KIND = 'agent_branch';

function parseParty(raw: any): AgentHandoffParty {
  return {
    id: raw?.id ?? null,
    name: raw?.name || 'Assistant',
    avatar_color: raw?.avatar_color ?? null,
    avatar_variant: raw?.avatar_variant ?? null,
  };
}

/**
 * Extract the handoff payload from a raw backend message, or undefined when
 * the message isn't a marker. Returns undefined rather than a partial object
 * for malformed metadata — a card with no destination is worse than no card,
 * because it looks clickable and goes nowhere.
 */
export function parseAgentHandoff(messageMetadata: any): AgentHandoff | undefined {
  if (!messageMetadata || messageMetadata.kind !== HANDOFF_MARKER_KIND) return undefined;
  const raw = messageMetadata.agent_branch;
  if (!raw || !raw.target_conversation_id) return undefined;
  if (raw.direction !== 'inbound' && raw.direction !== 'outbound') return undefined;

  return {
    direction: raw.direction,
    from: parseParty(raw.from),
    to: parseParty(raw.to),
    target_conversation_id: String(raw.target_conversation_id),
    target_message_id: raw.target_message_id ? String(raw.target_message_id) : undefined,
    branch_point_message_id: raw.branch_point_message_id
      ? String(raw.branch_point_message_id)
      : undefined,
  };
}
